import { NoraCheckboxInput } from 'ethos-design-system';
import React, { Dispatch, SetStateAction } from 'react';
import { Spacer } from '../../../common/components/v2';
import { Resource } from '../../../common/constants/resource';
import { IRiderInfo } from '../interfaces/IRiderInfo';
import { RequiredFieldTitle } from './RequiredFieldTitle';
import type { RiderExclusivityRule } from './riderExclusivity';
import styles from './styles.module.scss';

const Riders = ({
  riders,
  setRiders,
  exclusivityRules = [],
}: {
  riders: IRiderInfo[];
  setRiders: Dispatch<SetStateAction<IRiderInfo[]>>;
  exclusivityRules?: RiderExclusivityRule[];
}) => {
  // Function to toggle eligibility status for a given rider.
  // When the changed rider is a source in exclusivity rules, target riders are updated (e.g. mutually exclusive).
  const setEligibility = (riderName: string, isEligible: boolean) => {
    setRiders(prevAllRiders => {
      const rulesForSource = exclusivityRules.filter(
        r => r.sourceRider === riderName,
      );
      const targetUpdates = new Map<string, boolean>();
      for (const rule of rulesForSource) {
        targetUpdates.set(
          rule.targetRider,
          rule.inverse ? !isEligible : isEligible,
        );
      }
      return prevAllRiders.map(rider => {
        if (rider.rider === riderName) {
          return { ...rider, isEligible };
        }
        const targetEligible = targetUpdates.get(rider.rider);
        if (targetEligible !== undefined) {
          return { ...rider, isEligible: targetEligible };
        }
        return rider;
      });
    });
  };

  // Handler to update the input text for a rider
  const handleInputChange = (rider: string, text: string) => {
    setRiders(prevAllRiders =>
      prevAllRiders.map(riderObj =>
        riderObj.rider === rider ? { ...riderObj, reason: text } : riderObj,
      ),
    );
  };

  return (
    <div className={styles.Riders}>
      <RequiredFieldTitle title={Resource.RIDERS} />
      <Spacer height={8} />
      {/* Render riders with checkboxes */}
      {riders.map(riderObj => (
        <div
          key={riderObj.rider}
          className={`${styles.ChecklistItem} ${
            !riderObj.isUnderwritable ? styles.ChecklistItemDisabled : ''
          }`}
        >
          <NoraCheckboxInput
            name={riderObj.rider}
            data-tid={riderObj.rider}
            checked={riderObj.isEligible}
            disabled={!riderObj.isUnderwritable}
            validator={val => {
              const isEligible = !!val;
              setEligibility(riderObj.rider, isEligible);
              return '';
            }} // Call the toggle function on checkbox change
          >
            <div className={styles.ChecklistOption}>{riderObj.rider}</div>
          </NoraCheckboxInput>
          {riderObj.isUnderwritable && !riderObj.isEligible && (
            <input
              type="text"
              value={riderObj.reason}
              onChange={e => handleInputChange(riderObj.rider, e.target.value)}
              placeholder="Ineligibility reasons"
              className={styles.reason}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default Riders;
